import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { toast as sonnerToast } from "sonner";
import { useAuth } from "@/components/AuthProvider";
import { sanitizeInput, isValidInput } from "@/utils/security";
import { Separator } from "@/components/ui/separator";
import { Loader2 } from "lucide-react";
import { getEnvironmentConfig, detectEnvironment } from "@/utils/environmentUtils";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signInWithGoogle } = useAuth();
  const environment = detectEnvironment();
  const config = getEnvironmentConfig(environment);

  useEffect(() => {
    document.title = "Sign In | Lead Extract";
    
    // Extract hash parameters from URL if present (from OAuth redirect)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    
    // If we have an access token in the URL, it means we've been redirected back from OAuth
    if (accessToken) {
      console.log("Detected access token in URL, handling OAuth callback");
      // Clear the URL to remove the token
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Check if we have a session automatically
      supabase.auth.getSession().then(({ data }) => {
        if (data.session) {
          console.log("Valid session found after OAuth redirect");
          navigate('/dashboard', { replace: true });
        }
      });
    } else {
      // Normal session check for direct visitors
      const checkExistingSession = async () => {
        try {
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error("Error checking session:", error.message);
            return;
          }
          
          if (data.session) {
            console.log("User already has a valid session, redirecting to dashboard");
            const from = (location.state as any)?.from?.pathname || "/dashboard";
            navigate(from, { replace: true });
          }
        } catch (err) {
          console.error("Unexpected error checking session:", err);
        }
      };
      
      checkExistingSession();
    }
  }, [navigate, location.state]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && isValidInput(email);
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors({});
    const formData = new FormData(e.currentTarget);
    
    const rawEmail = formData.get("email") as string;
    const password = formData.get("password") as string;
    
    const errors: {
      email?: string;
      password?: string;
    } = {};
    
    if (!validateEmail(rawEmail)) {
      errors.email = "Please enter a valid email address";
      setFormErrors(errors);
      return;
    }
    
    if (!password || password.length < 6) {
      errors.password = "Password must be at least 6 characters";
      setFormErrors(errors);
      return;
    }
    
    setLoading(true);
    
    const email = sanitizeInput(rawEmail);
    
    try {
      console.log("Attempting to sign in with email:", email);
      
      await signIn(email, password);
      
      console.log("Login successful via AuthContext");
      
      const from = (location.state as any)?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error("Login error:", err.message || err);
      
      // Handle different types of auth errors with Sonner toast instead of Alert
      if (err.message?.toLowerCase().includes("invalid login") || 
          err.message?.toLowerCase().includes("invalid credentials") ||
          err.message?.toLowerCase().includes("invalid password")) {
        sonnerToast.error("Invalid email or password. Please try again.");
      } else if (err.message?.includes("Email not confirmed")) {
        sonnerToast.error("Please check your email to confirm your account before logging in. If you haven't received the confirmation email, you can request a new one or contact support.");
      } else {
        sonnerToast.error(err.message || "An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      console.log("Initiating Google sign in with redirect to:", config.siteUrl);
      
      // Make sure we're using Google sign-in with explicit redirect to https://app.leadextract.io/dashboard
      await signInWithGoogle();
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      sonnerToast.error(error.message || "Failed to sign in with Google. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors({});
    const formData = new FormData(e.currentTarget);
    
    const rawEmail = formData.get("email") as string;
    
    if (!validateEmail(rawEmail)) {
      setFormErrors({
        email: "Please enter a valid email address"
      });
      return;
    }
    
    setLoading(true);
    
    const email = sanitizeInput(rawEmail);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${config.siteUrl}/login`,
      });

      if (error) {
        console.error("Password reset error:", error.message);
        sonnerToast.error(error.message || "Failed to send password reset email. Please try again.");
      } else {
        console.log("Password reset email sent successfully");
        sonnerToast.success("We've sent you a password reset link. Please check your email.");
        setIsResetMode(false);
      }
    } catch (err) {
      console.error("Unexpected password reset error:", err);
      sonnerToast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-white">
      <Card className="w-full max-w-md">
        <div className="flex justify-center pt-6">
          <Logo size="lg" showText={false} />
        </div>
        {!isResetMode ? (
          <form onSubmit={handleSignIn}>
            <CardHeader>
              <CardTitle className="text-black">Log In</CardTitle>
              <CardDescription className="text-black">Enter your email below to login to your account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signin-email" className="text-black">Email</Label>
                <Input 
                  id="signin-email" 
                  name="email" 
                  type="email" 
                  placeholder="m@example.com" 
                  required 
                  className={formErrors.email ? "border-red-500" : ""} 
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="signin-password" className="text-black">Password</Label>
                <Input 
                  id="signin-password" 
                  name="password" 
                  type="password" 
                  required 
                  className={formErrors.password ? "border-red-500" : ""} 
                />
                {formErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
                )}
              </div>
              
              <button
                type="button"
                onClick={() => setIsResetMode(true)}
                className="text-sm text-black hover:underline"
              >
                Forgot your password?
              </button>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full bg-black hover:bg-black/90" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : "Sign In"}
              </Button>
              
              <div className="flex items-center w-full gap-4 my-2">
                <Separator className="flex-1" />
                <span className="text-sm text-gray-500">or</span>
                <Separator className="flex-1" />
              </div>
              
              <Button 
                type="button" 
                variant="outline" 
                className="w-full border-gray-300 hover:bg-gray-50 flex items-center justify-center gap-2" 
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
              >
                {googleLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connecting to Google...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512" width="16" height="16">
                      <path d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z" fill="#4285F4" />
                    </svg>
                    Sign in with Google
                  </>
                )}
              </Button>
              
              <p className="text-sm text-black">
                Don't have an account?{" "}
                <Link to="/signup" className="text-black hover:underline">
                  Sign up
                </Link>
              </p>
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <CardHeader>
              <CardTitle className="text-black">Reset Password</CardTitle>
              <CardDescription className="text-black">Enter your email to receive a password reset link.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-black">Email</Label>
                <Input 
                  id="reset-email" 
                  name="email" 
                  type="email" 
                  placeholder="m@example.com" 
                  required 
                  className={formErrors.email ? "border-red-500" : ""} 
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full bg-black hover:bg-black/90" disabled={loading}>
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
              <button
                type="button"
                onClick={() => setIsResetMode(false)}
                className="text-sm text-black hover:underline"
              >
                Back to login
              </button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
};

export default Login;
